import { TranslateService } from '@ngx-translate/core';
import { inject, Injectable } from '@angular/core';
import { Md5 } from 'ts-md5';
import { AuthService } from '../../auth/services/auth.service';
import { TrackingRecord, TrackingSuiteService } from '@siemens/tracking-suite-angular-lib';
import { IUserData, UserService } from './user.service';
import { Subscription } from 'rxjs';
import { Router } from '@angular/router';
import { environment } from '../../../environments/environment';
import { TokenClaims } from '../interfaces/token-claims.interface';

@Injectable({
    providedIn: 'root'
})
export class TrackingService {
    private translate = inject(TranslateService);
    private userService = inject(UserService);
    private readonly trackingSuiteService = inject(TrackingSuiteService);
    private authService = inject(AuthService);
    private router = inject(Router);

    public previousPath: string = '';
    public user: TokenClaims | null = null;
    public userData: IUserData | null = null;
    public guid: string = ''; // hashed from GID
    public visitToken: string; // hashed from accessToken
    public engTranslations: any;
    public rawAccessToken: string;
    public subs: Subscription[] = [];

    constructor() {
        (window as any)['ste_statistic'] = (window as any)['ste_statistic'] || [];
        const accessToken = this.authService.idToken();
        this.rawAccessToken = accessToken || '';
        this.visitToken = Md5.hashStr(this.rawAccessToken);
        this.loadEnglishTranslsations();
    }

    private ensureUserData(): void {
        if (!this.userData) {
            this.userData = this.userService.getIDTokenUserData();
            this.guid = this.userData ? Md5.hashStr(this.userData.gid) : '';
        }
    }

    private loadEnglishTranslsations(): void {
        this.subs.push(
            this.translate.getTranslation('en').subscribe((value) => {
                if (value) {
                    this.engTranslations = value;
                }
            })
        );
    }

    async addWCSTrackingItem(itemType: 'route' | 'action' | 'language', item: string) {
        if (!this.authService.isAuthenticated() || !this.authService.idToken()) {
            return;
        }

        this.rawAccessToken = this.authService.idToken() || '';
        this.visitToken = Md5.hashStr(this.rawAccessToken);

        if (!this.user) {
            this.user = this.authService.userIdTokenClaims();
        }
        this.ensureUserData();

        const newTrackingRecord = this.setUpNewRecordFormat(itemType, item);
        this.useNewTrackingService(newTrackingRecord);
    }

    private setUpNewRecordFormat(itemType: 'route' | 'action' | 'language', item: string): TrackingRecord {
        let action: 'click' | 'navigate' = 'click';
        if (itemType === 'route') {
            action = 'navigate';
        } else {
            if (this.router.url.includes('work-simulations')) {
                item = itemType + ' - ' + 'work-sim: ' + item;
            } else {
                item = itemType + ' - ' + item;
            }
        }
        return {
            accessToken: this.rawAccessToken,
            userGid: this.guid,
            actionType: action,
            actionValue: item,
            orgCode: this.userData && this.userData.orgCode ? this.userData.orgCode : 'unset',
            language: this.translate.currentLang ?? 'en',
            userCountry: this.userData && this.userData.country ? this.userData.country : 'unset'
        };
    }

    private useNewTrackingService(record: TrackingRecord): void {
        this.trackingSuiteService.send(record).subscribe();
    }
}
